import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { SupportService } from '@/app/core/services/support.service';

/** Imagenes que acepta el backend, y cuanto pesa cada una como maximo. */
const TIPOS_PERMITIDOS = ['image/png', 'image/jpeg', 'image/jfif', 'image/avif', 'image/webp'];
const MAXIMO_ARCHIVOS = 5;
const MAXIMO_BYTES = 10 * 1024 * 1024;

/** En que paso esta el formulario. */
type Paso = 'formulario' | 'enviando' | 'listo';

/** Que campos del formulario ya perdieron el foco al menos una vez. */
interface CamposTocados {
  orderNumber: boolean;
  requesterName: boolean;
  requesterPhone: boolean;
  description: boolean;
}

/**
 * Boton flotante de soporte.
 *
 * Un cliente con un problema no busca un menu: busca a alguien. Esto le pone la
 * puerta siempre a la vista, en cualquier pantalla, con o sin sesion iniciada.
 *
 * El formulario pide lo minimo —numero de orden, nombre, telefono y que le
 * pasa— porque cada campo de mas es una excusa para abandonarlo y terminar
 * escribiendo por WhatsApp, que es justo lo que esto viene a ordenar.
 */
@Component({
  selector: 'app-support-widget',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './support-widget.component.html',
  styleUrl: './support-widget.component.scss',
})
export class SupportWidgetComponent implements OnDestroy {
  private supportService = inject(SupportService);
  // App ZONELESS: sin `markForCheck` la respuesta llega y la pantalla no cambia.
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  abierto = false;
  paso: Paso = 'formulario';

  orderNumber = '';
  requesterName = '';
  requesterPhone = '';
  description = '';
  requesterEmail = '';

  archivos: File[] = [];
  /** Previsualizaciones, para que el cliente vea que adjunto lo que queria. */
  previews: string[] = [];

  error = '';
  numeroDeTicket = '';

  readonly maximoArchivos = MAXIMO_ARCHIVOS;

  /**
   * Que campo ya perdio el foco.
   *
   * El error no se muestra hasta que el cliente pasa por el campo: mostrarlos
   * todos apenas se abre el formulario es señalarle cuatro errores por algo
   * que todavia ni empezo a llenar.
   */
  tocado: CamposTocados = {
    orderNumber: false,
    requesterName: false,
    requesterPhone: false,
    description: false,
  };

  marcarTocado(campo: keyof CamposTocados): void {
    this.tocado[campo] = true;
  }

  get errorOrderNumber(): string {
    if (!this.tocado.orderNumber) return '';
    return this.orderNumber.trim().length > 0 ? '' : 'Ingresa tu número de orden.';
  }

  get errorNombre(): string {
    if (!this.tocado.requesterName) return '';
    return this.requesterName.trim().length >= 3 ? '' : 'El nombre debe tener al menos 3 letras.';
  }

  get errorTelefono(): string {
    if (!this.tocado.requesterPhone) return '';
    return this.telefonoValido(this.requesterPhone)
      ? ''
      : 'Coloca el número de teléfono bien, con el código de tu país.';
  }

  get errorDescripcion(): string {
    if (!this.tocado.description) return '';
    return this.description.trim().length >= 16
      ? ''
      : 'Cuéntanos qué pasó en al menos 16 caracteres.';
  }

  /** Numeros, con o sin +, espacios, guiones o parentesis entre medio. */
  private telefonoValido(valor: string): boolean {
    const limpio = valor.replace(/[\s()-]/g, '');
    return /^\+?\d{7,15}$/.test(limpio);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.liberarPreviews();
  }

  /** Cierra con Escape: es lo que espera cualquiera que abre algo encima. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.abierto) this.cerrar();
  }

  abrir(): void {
    this.abierto = true;
    this.paso = 'formulario';
    this.error = '';
  }

  cerrar(): void {
    this.abierto = false;
    // Si ya lo mando, la proxima vez arranca limpio. Si estaba a medio llenar,
    // se le respeta lo escrito: cerrar sin querer no puede costarle el texto.
    if (this.paso === 'listo') this.limpiar();
  }

  /** El fondo oscuro cierra; el panel no, o se pierde todo con un click al lado. */
  onFondoClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.cerrar();
  }

  onArchivos(event: Event): void {
    const input = event.target as HTMLInputElement;
    const nuevos = Array.from(input.files ?? []);
    this.error = '';

    for (const archivo of nuevos) {
      if (this.archivos.length >= MAXIMO_ARCHIVOS) {
        this.error = `Puedes adjuntar hasta ${MAXIMO_ARCHIVOS} imágenes.`;
        break;
      }
      if (!TIPOS_PERMITIDOS.includes(archivo.type)) {
        this.error = 'Solo se pueden adjuntar imágenes.';
        continue;
      }
      if (archivo.size > MAXIMO_BYTES) {
        this.error = `"${archivo.name}" pesa más de 10 MB.`;
        continue;
      }
      this.archivos.push(archivo);
      this.previews.push(URL.createObjectURL(archivo));
    }

    // Se limpia el input o volver a elegir el MISMO archivo no dispara el evento.
    input.value = '';
  }

  quitarArchivo(indice: number): void {
    URL.revokeObjectURL(this.previews[indice]);
    this.archivos.splice(indice, 1);
    this.previews.splice(indice, 1);
  }

  /** Lo minimo para que el boton de enviar tenga sentido. */
  get puedeEnviar(): boolean {
    return (
      this.paso === 'formulario' &&
      this.orderNumber.trim().length > 0 &&
      this.requesterName.trim().length >= 3 &&
      this.telefonoValido(this.requesterPhone) &&
      this.description.trim().length >= 16
    );
  }

  enviar(): void {
    if (!this.puedeEnviar) {
      // Intento de envio invalido: recien aca se le muestran los cuatro
      // errores juntos, si los tiene, en vez de dejarlo adivinar por que
      // el boton no hizo nada.
      this.tocado = {
        orderNumber: true,
        requesterName: true,
        requesterPhone: true,
        description: true,
      };
      return;
    }

    this.paso = 'enviando';
    this.error = '';

    this.supportService
      .createTicket({
        orderNumber: this.orderNumber.trim(),
        requesterName: this.requesterName.trim(),
        requesterPhone: this.requesterPhone.trim(),
        description: this.description.trim(),
        ...(this.requesterEmail.trim() ? { requesterEmail: this.requesterEmail.trim() } : {}),
        files: this.archivos,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (respuesta) => {
          this.numeroDeTicket = respuesta.number;
          this.paso = 'listo';
          this.cdr.markForCheck();
        },
        error: (err) => {
          // El backend explica en castellano por que no pudo; si no llega nada
          // util, un mensaje generico antes que un codigo que no dice nada.
          this.error =
            err?.error?.message ||
            'No pudimos enviar tu solicitud. Vuelve a intentarlo en un momento.';
          this.paso = 'formulario';
          this.cdr.markForCheck();
        },
      });
  }

  private limpiar(): void {
    this.orderNumber = '';
    this.requesterName = '';
    this.requesterPhone = '';
    this.description = '';
    this.requesterEmail = '';
    this.liberarPreviews();
    this.archivos = [];
    this.previews = [];
    this.numeroDeTicket = '';
    this.error = '';
    this.paso = 'formulario';
    this.tocado = {
      orderNumber: false,
      requesterName: false,
      requesterPhone: false,
      description: false,
    };
  }

  /** Las URL de objeto quedan en memoria hasta que se revocan a mano. */
  private liberarPreviews(): void {
    for (const url of this.previews) URL.revokeObjectURL(url);
  }
}